import { useState } from "react"
import { useForm } from "react-hook-form"
import { toast } from "sonner"
import type { AxiosError } from "axios"
import { Loader2 } from "lucide-react"

import type { ITrait } from "@apis/traits/types"
import { useBulkAddTraitUploadMutation } from "@apis/traits/useTraits"
import { ConfirmDialog } from "@components/ConfirmDialog/ConfirmDialog"
import { Button } from "@components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@components/ui/card"
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@components/ui/form"
import { Input } from "@components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@components/ui/select"

type TFileUploadFormProps = {
  traits: ITrait[]
}

type TFileUploadFormData = {
  trait_code: string
  reason?: string
  file: File | null
}

export const FileUploadForm = ({ traits }: TFileUploadFormProps) => {
  const [isConfirmOpen, setIsConfirmOpen] = useState(false)
  // Remounting the file input is what clears the file the browser is holding on to.
  const [fileInputKey, setFileInputKey] = useState(0)

  const form = useForm<TFileUploadFormData>({
    mode: "onChange",
    defaultValues: { trait_code: "", reason: "", file: null },
  })
  const bulkAddTraitUploadMutation = useBulkAddTraitUploadMutation()

  const traitCode = form.watch("trait_code")
  const file = form.watch("file")
  const traitName =
    traits.find((trait) => trait.trait_code === traitCode)?.trait_name ??
    traitCode

  const handleConfirm = () => {
    const { trait_code, reason } = form.getValues()

    if (!file) return

    const formData = new FormData()
    formData.append("file", file)
    formData.append("trait_code", trait_code)
    if (reason) formData.append("reason", reason)

    bulkAddTraitUploadMutation.mutate(formData, {
      onSuccess: () => {
        // Sticky: the result lands on Slack later, so the user should not miss this.
        toast.success(
          "File received! The result will be posted to #inspa-ops once it is processed.",
          { duration: Infinity },
        )
        form.reset()
        setFileInputKey((key) => key + 1)
      },
      onError: (error: string | AxiosError) =>
        toast.error(`Error uploading file! ${error}`),
      onSettled: () => setIsConfirmOpen(false),
    })
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>File Upload</CardTitle>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(() => setIsConfirmOpen(true))}
            className="tw:grid tw:grid-cols-12 tw:items-baseline tw:gap-4"
          >
            <div className="tw:col-span-12 tw:md:col-span-6">
              <FormField
                control={form.control}
                name="trait_code"
                rules={{ required: "Trait is required" }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Trait</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl className="tw:w-full">
                        <SelectTrigger>
                          <SelectValue placeholder="Select Trait" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {traits.map((trait) => (
                          <SelectItem
                            key={trait.trait_code}
                            value={trait.trait_code}
                          >
                            {trait.trait_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="tw:col-span-12 tw:md:col-span-6">
              <FormField
                control={form.control}
                name="reason"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Reason (optional)</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Reason"
                        name={field.name}
                        value={field.value ?? ""}
                        onBlur={field.onBlur}
                        onChange={field.onChange}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="tw:col-span-12">
              <FormField
                control={form.control}
                name="file"
                rules={{ required: "A .csv file is required" }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel htmlFor="bulk-add-traits-file-input">
                      Attach File
                    </FormLabel>
                    <FormControl>
                      <Input
                        key={fileInputKey}
                        id="bulk-add-traits-file-input"
                        type="file"
                        accept=".csv"
                        name={field.name}
                        onBlur={field.onBlur}
                        onChange={(event) =>
                          field.onChange(event.target.files?.[0] ?? null)
                        }
                      />
                    </FormControl>
                    <FormDescription>
                      File must be in .csv format with an &apos;account_id&apos;
                      column. The selected trait is applied to every account in
                      the file.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="tw:col-span-12 tw:flex tw:justify-end tw:gap-4">
              <Button
                type="submit"
                disabled={bulkAddTraitUploadMutation.isPending}
              >
                {bulkAddTraitUploadMutation.isPending && (
                  <Loader2 className="tw:animate-spin" />
                )}
                Upload File
              </Button>
            </div>
          </form>
        </Form>
      </CardContent>

      <ConfirmDialog
        open={isConfirmOpen}
        onOpenChange={setIsConfirmOpen}
        title="Add this trait to every account in the file?"
        description={`This will add ${traitName} to every account in ${file?.name ?? "the file"}.`}
        confirmLabel="Upload file"
        isPending={bulkAddTraitUploadMutation.isPending}
        onConfirm={handleConfirm}
      />
    </Card>
  )
}
